import Leaflet from 'leaflet'
import { Feature, View } from 'protomaps-leaflet'

import { LocatedFeature, queryPointFeatures } from './map-feature-source'
import { EMPTY_MAP_MARKERS, MapMarkerModel } from './map-model'
import { computeLabelPlacements, LabelPlacement, POI_ICON_SIZE } from './map-poi-label-placement'
import { PoiMarker } from './PoiMarker'

const POI_DATA_LAYER = 'poi'
const POI_MIN_ZOOM = 15
const POI_MARKER_SIZE: [number, number] = [POI_ICON_SIZE, POI_ICON_SIZE]
const POI_MARKER_ANCHOR: [number, number] = [POI_ICON_SIZE / 2, POI_ICON_SIZE / 2]

// class=hospital cobre as subclasses hospital, clinic e nursing_home no schema do tileset (OpenMapTiles/Planetiler)
const POI_HEALTH_CLASSES = ['hospital', 'pharmacy']

export function observePoiMarkers(
  map: Leaflet.Map,
  view: View,
  onMarkersChange: (markers: MapMarkerModel[]) => void,
  onError: (error: unknown) => void
): () => void {
  let requestId = 0

  const refresh = async () => {
    const currentRequestId = ++requestId

    if (map.getZoom() < POI_MIN_ZOOM) {
      onMarkersChange(EMPTY_MAP_MARKERS)
      return
    }

    const zoom = Math.round(map.getZoom())

    try {
      const located = await queryPointFeatures(view, map, POI_DATA_LAYER, zoom, isHealthPoi)
      if (currentRequestId === requestId) {
        onMarkersChange(toPoiMarkers(located, map))
      }
    } catch (error) {
      if (currentRequestId === requestId && !isAbortError(error)) {
        onError(error)
      }
    }
  }

  map.on('moveend', refresh)
  refresh()

  return () => {
    requestId++
    map.off('moveend', refresh)
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}

function getPoiClass(feature: Feature): string {
  return String(feature.props.class ?? '')
}

function isHealthPoi(feature: Feature): boolean {
  return POI_HEALTH_CLASSES.includes(getPoiClass(feature))
}

interface PoiCandidate {
  id: string
  name?: string
  coordinates: [number, number]
  point: { x: number; y: number }
}

function toPoiMarkers(located: LocatedFeature[], map: Leaflet.Map): MapMarkerModel[] {
  const candidates = located
    .map((feature) => toPoiCandidate(feature, map))
    .sort((first, second) => first.id.localeCompare(second.id))
  const viewportSize = map.getSize()
  const placements = computeLabelPlacements(candidates, { width: viewportSize.x, height: viewportSize.y })

  return candidates.map((candidate) => toPoiMarker(candidate, placements.get(candidate.id)))
}

function toPoiCandidate(located: LocatedFeature, map: Leaflet.Map): PoiCandidate {
  const poiClass = getPoiClass(located.feature)
  const name = located.feature.props.name ? String(located.feature.props.name) : undefined

  return {
    id: `${poiClass}-${located.latLng.lat}-${located.latLng.lng}-${name ?? ''}`,
    name,
    coordinates: [located.latLng.lng, located.latLng.lat],
    point: map.latLngToContainerPoint(located.latLng),
  }
}

function toPoiMarker(candidate: PoiCandidate, placement: LabelPlacement | undefined): MapMarkerModel {
  return {
    id: candidate.id,
    coordinates: candidate.coordinates,
    size: POI_MARKER_SIZE,
    anchor: POI_MARKER_ANCHOR,
    content: (
      <PoiMarker
        name={placement ? candidate.name : undefined}
        labelPosition={placement?.position}
        labelLines={placement?.lines}
      />
    ),
  }
}
